import { faShop } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { MERCHANT_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useMemo, useState } from "react"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { MRT_ColumnDef } from "material-react-table"
import { fetchCustomJobs, FetchCustomJobsResponse, MerchantSettingsCustomJobQuery } from "../customJobApi"
import { MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { updateCustomJob } from "../../../../accounting/accounting/merchantCustomReports/customReportsApi"
import { StandardRunCustomJobDialog } from "../../../../accounting/accounting/merchantCustomReports/components/StandardRunCustomJobDialog"
import {
    CustomJob,
    CustomReportsType,
} from "../../../../accounting/accounting/merchantCustomReports/customReportsTypes"

type Props = Readonly<{}>

const useStyles = makeStyles()((theme) => ({
    activateText: {
        color: theme.palette.tailwind.green[600],
    },
    inactivateText: {
        color: theme.palette.tailwind.red[600],
    },
}))

const transformData = (data: FetchCustomJobsResponse) => {
    return data.customJobs
}

export function MerchantCustomJobs({}: Props) {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()
    const [showRunJobDialog, setShowRunJobDialog] = useState(false)
    const [selectedCustomJob, setSelectedCustomJob] = useState<CustomJob>()

    const customJobs = useApiQuery(fetchCustomJobs, {
        queryName: MerchantSettingsCustomJobQuery.CUSTOM_JOBS_LIST,
        dependencies: {
            merchantId,
            type: CustomReportsType.IMPORT,
        },
        select: transformData,
    })

    const { mutate: callUpdateCustomJob } = useApiMutation(updateCustomJob, {
        async onSuccess(_params, _data, context) {
            await context.queryClient.invalidateQueries({
                queryKey: [MerchantSettingsCustomJobQuery.CUSTOM_JOBS_LIST],
            })
        },
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(customJobs)

    const columns = useMemo<MRT_ColumnDef<CustomJob>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("merchant-admin-settings-custom-jobs-table-name"),
                accessorKey: "name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("merchant-admin-settings-custom-jobs-table-interval"),
                accessorKey: "interval",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("merchant-admin-settings-custom-jobs-table-last-run"),
                    accessorKey: "lastRunTime",
                },
                (row) => row?.lastRunTime,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("merchant-admin-settings-custom-jobs-table-status"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <LocalizedStrict id="merchant-admin-settings-custom-jobs-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_ADMIN_SETTINGS_CUSTOM_JOBS}
                    title="Custom jobs"
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-settings-header">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-custom-jobs">
                            <Breadcrumb>Custom jobs</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                initialState={{
                    density: "spacious",
                }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="merchant-admin-settings-custom-jobs-table-action-details">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                                //todo
                            }}
                            key={`details-${row.original.id}`}
                        >
                            Details
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="merchant-admin-settings-custom-jobs-table-action-run-job">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                                setSelectedCustomJob(row.original)
                                setShowRunJobDialog(true)
                            }}
                            key={`run-job-${row.original.id}`}
                        >
                            Run job
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict
                        id={
                            row.original.isActive
                                ? "merchant-admin-settings-custom-jobs-table-action-inactivate"
                                : "merchant-admin-settings-custom-jobs-table-action-activate"
                        }
                    >
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                                callUpdateCustomJob({
                                    jobId: row.original.id,
                                    isActive: !row.original.isActive,
                                })
                            }}
                            className={row.original.isActive ? classes.inactivateText : classes.activateText}
                            key={`status-${row.original.id}`}
                        >
                            {row.original.isActive ? "Inactivate" : "Activate"}
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
            />

            {showRunJobDialog && selectedCustomJob && (
                <StandardRunCustomJobDialog
                    item={selectedCustomJob}
                    open={showRunJobDialog}
                    onClose={() => setShowRunJobDialog(false)}
                />
            )}
        </>
    )
}
